import {
    DetalizationType,
    InviteLinksChartType,
    UsersSortType,
} from '@/features/analytics/constants'
import { useInviteLinksStore } from '@/features/analytics/model'
import { generateUniqueColor } from '@/features/analytics/utils'
import {
    InviteLinksGroupChannelsType,
    InviteLinkWithAnalyticsType,
} from '@/shared/api'
import { InviteLinkAnalytics } from '@synapex/prisma/client'
import { useMemo } from 'react'
import { LegendType } from 'recharts'

interface InviteLinksGroupsDataPoint {
    date: string
    [key: string]: number | string
}

interface InviteLinksGroupsType {
    id: number
    name?: string
    value?: string
    color: string
    type: LegendType
    inviteLinks: InviteLinkWithAnalyticsType[]
}

export const useInviteLinksGroupsChartData = ({
    groups,
    channelId,
}: {
    groups: InviteLinksGroupChannelsType[]
    channelId: number
}) => {
    const {
        // selectedChartMetricGroup,
        selectedGroups,
        detalizationSortGroup,
        usersSortGroup,
        startDateGroup,
        endDateGroup,
        selectedChartMetric,
    } = useInviteLinksStore()

    const inviteLinksGroups = useMemo(() => {
        // const groupEntries = Object.entries(selectedGroups)
        const groupEntries = selectedGroups[channelId] || []
        const result = groupEntries.map((groupId) => ({
            id: groupId,
            name: groups.find(
                (group) => group.invite_links_group_id === groupId
            )?.group_name,
            value: groups.find(
                (group) => group.invite_links_group_id === groupId
            )?.group_name,
            color: generateUniqueColor(groupId),
            type: 'line' as LegendType,
            inviteLinks:
                groups.find((group) => group.invite_links_group_id === groupId)
                    ?.group_name === 'Все ссылки'
                    ? (groups.map((item) => item.inviteLinks).flat() ?? [])
                    : (groups.find(
                          (group) => group.invite_links_group_id === groupId
                      )?.inviteLinks ?? []),
        }))

        return result.map((item) => {
            if (item.name === 'Все ссылки') {
                item.inviteLinks = item.inviteLinks.map((link: any) => ({
                    ...link,
                    invite_links_group_id: item.id,
                }))
            }
            return item
        })
    }, [selectedGroups, groups])

    const inviteLinksData = useMemo(() => {
        if (inviteLinksGroups.length === 0) return []
        const processedGroups = processInviteLinksGroups(
            inviteLinksGroups
            // endDateGroup
        )
        return calculateInviteLinksGroupsData(
            processedGroups,
            selectedChartMetric.value,
            usersSortGroup.value,
            detalizationSortGroup.value,
            startDateGroup,
            endDateGroup
        )
    }, [
        inviteLinksGroups,
        selectedChartMetric,
        usersSortGroup,
        detalizationSortGroup,
        startDateGroup,
        endDateGroup,
    ])
    return { inviteLinksData, inviteLinksGroups }
}

const processInviteLinksGroups = (
    groups: InviteLinksGroupsType[]
    // endDateGroup: string
) => {
    return groups.map((group) => {
        const processedInviteLinks = processInviteLinks(
            group.inviteLinks
            // endDateGroup
        )
        return {
            ...group,
            inviteLinks: processedInviteLinks,
            active_creative: null,
            active_cereative_id: null,
        }
    })
}

const processInviteLinks = (
    links: InviteLinkWithAnalyticsType[]
    // endDateInvite: string
): InviteLinkWithAnalyticsType[] => {
    const processedLinks = links.map((link) => {
        const linkCopy = { ...link }
        linkCopy.analytics = link.analytics ? [...link.analytics] : []
        let lastPostReachValue = 0
        linkCopy.analytics.sort(
            (a, b) =>
                new Date(a.created_at).getTime() -
                new Date(b.created_at).getTime()
        )
        for (let i = 1; i < linkCopy.analytics.length; i++) {
            const currentAnalytics = linkCopy.analytics[i]
            const previousAnalytics = linkCopy.analytics[i - 1]
            currentAnalytics.subscribed_count_delta = Math.max(
                currentAnalytics.subscribed_count -
                    previousAnalytics.subscribed_count,
                0
            )
            currentAnalytics.subscribed_today = Math.max(
                currentAnalytics.subscribed_count +
                    currentAnalytics.unsubscribed_count -
                    (previousAnalytics.subscribed_count +
                        previousAnalytics.unsubscribed_count),
                0
            )
            currentAnalytics.unsubscribed_today = Math.max(
                currentAnalytics.unsubscribed_count -
                    previousAnalytics.unsubscribed_count,
                0
            )
            currentAnalytics.premium_subscribed_today = Math.max(
                currentAnalytics.premium_subscribed_count -
                    previousAnalytics.premium_subscribed_count,
                0
            )
            currentAnalytics.premium_unsubscribed_today = Math.max(
                currentAnalytics.premium_unsubscribed_count -
                    previousAnalytics.premium_unsubscribed_count,
                0
            )
            currentAnalytics.without_premium_subscribed_today = Math.max(
                currentAnalytics.subscribed_count -
                    currentAnalytics.premium_subscribed_count -
                    (previousAnalytics.subscribed_count -
                        previousAnalytics.premium_subscribed_count),
                0
            )
            currentAnalytics.without_premium_unsubscribed_today = Math.max(
                currentAnalytics.unsubscribed_count -
                    currentAnalytics.premium_unsubscribed_count -
                    (previousAnalytics.unsubscribed_count -
                        previousAnalytics.premium_unsubscribed_count),
                0
            )
            if ((currentAnalytics.telegram_post_reach ?? 0) > 0) {
                lastPostReachValue = currentAnalytics.telegram_post_reach ?? 0
            }
            currentAnalytics.telegram_post_reach_delta = Math.max(
                (currentAnalytics.telegram_post_reach ?? lastPostReachValue) -
                    (previousAnalytics.telegram_post_reach ??
                        lastPostReachValue),
                0
            )
        }

        if (linkCopy.analytics.length > 0) {
            const firstAnalytics = linkCopy.analytics[0]
            firstAnalytics.subscribed_count_delta =
                firstAnalytics.subscribed_count_delta
            firstAnalytics.subscribed_today = firstAnalytics.subscribed_count
            firstAnalytics.unsubscribed_today =
                firstAnalytics.unsubscribed_count
            firstAnalytics.premium_subscribed_today =
                firstAnalytics.premium_subscribed_count
            firstAnalytics.premium_unsubscribed_today =
                firstAnalytics.premium_unsubscribed_count
            firstAnalytics.without_premium_subscribed_today =
                firstAnalytics.subscribed_count -
                firstAnalytics.premium_subscribed_count
            firstAnalytics.without_premium_unsubscribed_today =
                firstAnalytics.unsubscribed_count -
                firstAnalytics.premium_unsubscribed_count
        }
        return linkCopy
    })
    return processedLinks
}

const metricFieldAllUsersMap: { [key in InviteLinksChartType]: string } = {
    subscribedCount: 'subscribed_today',
    reach: 'telegram_post_reach_delta',
    CTR: 'ctr',
    CPM: 'cpm',
    CPS: 'cps',
    unsubscribedCount: 'unsubscribed_today',
    unsubscriptionPercentage: 'unsubscribed_percentage',
    total_CPS: 'total_cps',
    budget: 'budget',
    totalSubscribed: 'subscribed_count_delta',
}

const metricFieldPremiumUsersMap: { [key in InviteLinksChartType]: string } = {
    subscribedCount: 'premium_subscribed_today',
    reach: 'telegram_post_reach_delta',
    CTR: 'premium_ctr',
    CPM: 'cpm',
    CPS: 'premium_cps',
    unsubscribedCount: 'premium_unsubscribed_today',
    unsubscriptionPercentage: 'premium_unsubscribed_percentage',
    total_CPS: 'premium_total_cps',
    budget: 'budget',
    totalSubscribed: 'subscribed_count_delta',
}
const metricFieldWithoutPremiumUsersMap: {
    [key in InviteLinksChartType]: string
} = {
    subscribedCount: 'without_premium_subscribed_today',
    reach: 'telegram_post_reach_delta',
    CTR: 'without_premium_ctr',
    CPM: 'cpm',
    CPS: 'without_premium_cps',
    unsubscribedCount: 'without_premium_unsubscribed_today',
    unsubscriptionPercentage: 'without_premium_unsubscribed_percentage',
    total_CPS: 'without_premium_total_cps',
    budget: 'budget',
    totalSubscribed: 'subscribed_count_delta',
}

const metricAggregationType: {
    [key in InviteLinksChartType]: 'sum' | 'average'
} = {
    subscribedCount: 'sum',
    reach: 'sum',
    CTR: 'sum',
    CPM: 'sum',
    CPS: 'sum',
    unsubscribedCount: 'sum',
    unsubscriptionPercentage: 'sum',
    total_CPS: 'sum',
    budget: 'sum',
    totalSubscribed: 'sum',
}

// interface IntermediateDataPoint {
//     [linkId: string]: {
//         sum: number
//         count: number
//     }
// }

type NestedData = {
    [key: string]: {
        sum: number
        count: number
    }
}

const calculateInviteLinksGroupsData = (
    groups: InviteLinksGroupsType[],
    selectedChartMetric: InviteLinksChartType,
    userSortInviteLinks: UsersSortType,
    detalization: DetalizationType,
    startDate: string,
    endDate: string
): InviteLinksGroupsDataPoint[] => {
    let metricField = metricFieldAllUsersMap[selectedChartMetric]
    const aggregationType = metricAggregationType[selectedChartMetric]

    if (userSortInviteLinks === 'premiumUsers') {
        metricField = metricFieldPremiumUsersMap[selectedChartMetric]
    } else if (userSortInviteLinks === 'noPremium') {
        metricField = metricFieldWithoutPremiumUsersMap[selectedChartMetric]
    }

    const start = new Date(startDate)
    start.setHours(0, 0, 0, 0)
    const end = new Date(endDate)
    end.setHours(23, 59, 59, 999)
    const dataPointsMap = new Map<
        string,
        { [groupId: string]: { sum: number; count: number } }
    >()

    if (selectedChartMetric === 'budget') {
        const generateDatesInRange = (
            start: Date,
            end: Date,
            detalization: DetalizationType
        ): string[] => {
            const dates: string[] = []
            let currentDate = new Date(start)
            while (currentDate <= end) {
                dates.push(getPeriodKey(currentDate, detalization))
                currentDate = getNextDate(currentDate, detalization)
            }
            return dates
        }

        const getNextDate = (
            date: Date,
            detalization: DetalizationType
        ): Date => {
            const nextDate = new Date(date)
            switch (detalization) {
                case 'daily':
                    nextDate.setDate(nextDate.getDate() + 1)
                    break
                case 'weekly':
                    nextDate.setDate(nextDate.getDate() + 7)
                    break
                case 'monthly':
                    nextDate.setMonth(nextDate.getMonth() + 1)
                    break
                default:
                    throw new Error(
                        `Unsupported detalization type: ${detalization}`
                    )
            }
            return nextDate
        }

        for (const group of groups) {
            const groupId = group.id
            for (const link of group.inviteLinks.sort(
                (a, b) =>
                    new Date(a.published_at ?? a.created_at).getTime() -
                    new Date(b.published_at ?? b.created_at).getTime()
            )) {
                if (!link.published_at || link.status != 'published') continue
                const creationDate = new Date(link.published_at)
                // const now = new Date();

                const allDates = generateDatesInRange(
                    creationDate,
                    creationDate,
                    detalization
                )

                let lastSum = 0
                let lastCount = 0
                for (const dateKey of allDates) {
                    if (!dataPointsMap.has(dateKey)) {
                        dataPointsMap.set(dateKey, {})
                    }

                    const dataPoint = dataPointsMap.get(dateKey)!

                    if (!dataPoint[groupId]) {
                        dataPoint[groupId] = { sum: lastSum, count: lastCount }
                    }

                    if (dateKey === getPeriodKey(creationDate, detalization)) {
                        dataPoint[groupId].sum += link.budget
                        dataPoint[groupId].count += 1
                        lastSum = dataPoint[groupId].sum
                        lastCount = dataPoint[groupId].count
                    } else {
                        dataPoint[groupId].sum = lastSum
                        dataPoint[groupId].count = lastCount
                    }
                }
            }
        }
    } else if (selectedChartMetric === 'unsubscriptionPercentage') {
        const subDataPointsMap = new Map<
            string,
            { [groupId: string]: { sum: number; count: number } }
        >()
        const unsubDataPointsMap = new Map<
            string,
            { [groupId: string]: { sum: number; count: number } }
        >()
        for (const group of groups) {
            const groupId = group.id
            for (const link of group.inviteLinks) {
                if (!link.analytics) {
                    continue
                }
                for (const analytics of link.analytics) {
                    const analyticsDate = new Date(analytics.created_at)
                    if (analyticsDate < start || analyticsDate > end) {
                        continue
                    }

                    const dateKey = getPeriodKey(analyticsDate, detalization)

                    let subDataPoint = subDataPointsMap.get(dateKey)
                    let unsubDataPoint = unsubDataPointsMap.get(dateKey)
                    if (!subDataPoint) {
                        subDataPoint = {}
                        subDataPointsMap.set(dateKey, subDataPoint)
                    }
                    if (!unsubDataPoint) {
                        unsubDataPoint = {}
                        unsubDataPointsMap.set(dateKey, unsubDataPoint)
                    }

                    let subValue =
                        analytics.subscribed_count +
                        analytics.unsubscribed_count
                    let unsubValue = analytics.unsubscribed_count
                    if (typeof subValue === 'number') {
                        if (!subDataPoint[groupId]) {
                            subDataPoint[groupId] = { sum: 0, count: 0 }
                        }
                        const metricData = subDataPoint[groupId] as {
                            sum: number
                            count: number
                        }

                        if (metricField === 'ctr') {
                            subValue *= 100
                        }

                        metricData.sum += subValue
                        metricData.count += 1
                    }

                    if (typeof unsubValue === 'number') {
                        if (!unsubDataPoint[groupId]) {
                            unsubDataPoint[groupId] = { sum: 0, count: 0 }
                        }
                        const metricData = unsubDataPoint[groupId] as {
                            sum: number
                            count: number
                        }

                        if (metricField === 'ctr') {
                            unsubValue *= 100
                        }

                        metricData.sum += unsubValue
                        metricData.count += 1
                    }
                }
            }
        }

        for (const [date, data1] of unsubDataPointsMap.entries()) {
            if (subDataPointsMap.has(date)) {
                const data2 = subDataPointsMap.get(date)!
                const resultForDate: NestedData = {}
                for (const groupId in data1) {
                    const map1GroupData = data1[groupId]
                    const map2GroupData = data2[groupId]
                    if (map2GroupData && map2GroupData.sum !== 0) {
                        const ratio = map1GroupData.sum / map2GroupData.sum
                        resultForDate[groupId] = {
                            sum: ratio,
                            count: map1GroupData.count,
                        }
                    } else {
                        resultForDate[groupId] = {
                            sum: 0,
                            count: map1GroupData.count,
                        }
                    }
                }
                dataPointsMap.set(date, resultForDate)
            }
        }
    } else {
        for (const group of groups) {
            const groupId = group.id
            for (const link of group.inviteLinks) {
                if (!link.analytics) {
                    continue
                }
                for (const analytics of link.analytics) {
                    const analyticsDate = new Date(analytics.created_at)
                    if (analyticsDate < start || analyticsDate > end) {
                        continue
                    }

                    const dateKey = getPeriodKey(analyticsDate, detalization)

                    let dataPoint = dataPointsMap.get(dateKey)
                    if (!dataPoint) {
                        dataPoint = {}
                        dataPointsMap.set(dateKey, dataPoint)
                    }
                    let value =
                        analytics[metricField as keyof InviteLinkAnalytics]

                    if (typeof value === 'number') {
                        if (!dataPoint[groupId]) {
                            dataPoint[groupId] = { sum: 0, count: 0 }
                        }
                        const metricData = dataPoint[groupId] as {
                            sum: number
                            count: number
                        }

                        if (metricField === 'ctr') {
                            value *= 100
                        }

                        metricData.sum += value
                        metricData.count += 1
                    }
                }
            }
        }
    }

    // const allLinksGroup = groups.find((g) => g.name === 'Все ссылки')
    // if (allLinksGroup) {
    //     const allLinksGroupId = allLinksGroup.id
    //     for (const [_, dataPoint] of dataPointsMap.entries()) {
    //         let totalSum = 0
    //         let totalCount = 0
    //         for (const [groupId, metricData] of Object.entries(dataPoint)) {
    //             // if (groupId !== allLinksGroupId) {
    //                 totalSum += metricData.sum
    //                 totalCount += metricData.count
    //             // }
    //         }
    //         dataPoint[allLinksGroupId] = { sum: totalSum, count: totalCount }
    //     }
    // }

    const inviteLinksData: InviteLinksGroupsDataPoint[] = []

    for (const [dateKey, dataPoint] of dataPointsMap.entries()) {
        const finalDataPoint: InviteLinksGroupsDataPoint = { date: dateKey }
        for (const [groupId, metricData] of Object.entries(dataPoint)) {
            if (groupId !== 'index') {
                const { sum, count } = metricData as {
                    sum: number
                    count: number
                }
                let finalValue = 0
                if (aggregationType === 'sum') {
                    finalValue = sum
                } else if (aggregationType === 'average') {
                    finalValue = count > 0 ? sum / count : 0
                }
                if (metricField === 'ctr') {
                    // finalValue = Math.round((finalValue + Number.EPSILON) * 100) / 100;
                    finalValue = Number(
                        parseFloat(finalValue.toString()).toFixed(2)
                    )
                }
                if (metricField === 'unsubscribed_percentage') {
                    // finalValue = Math.round((finalValue + Number.EPSILON) * 100) / 100;
                    finalValue = Number(
                        parseFloat((finalValue * 100).toString()).toFixed(2)
                    )
                }
                if (
                    metricField === 'cpm' ||
                    metricField === 'cps' ||
                    metricField === 'total_cps'
                ) {
                    finalValue = Math.round(finalValue)
                }
                finalDataPoint[groupId] = finalValue
            }
        }
        inviteLinksData.push(finalDataPoint)
    }

    inviteLinksData.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )

    return inviteLinksData
}

const getPeriodKey = (date: Date, detalization: DetalizationType): string => {
    let key: string
    const dateCopy = new Date(date)
    const weekStart = new Date(dateCopy)

    switch (detalization) {
        case 'daily':
            key = dateCopy.toISOString().split('T')[0]
            break
        case 'weekly':
            weekStart.setDate(dateCopy.getDate() - dateCopy.getDay())
            key = weekStart.toISOString().split('T')[0]
            break
        case 'monthly':
            key = `${dateCopy.getFullYear()}-${String(dateCopy.getMonth() + 1).padStart(2, '0')}`
            break
        default:
            key = dateCopy.toISOString().split('T')[0]
    }
    return key
}
